import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { LinkedInProfileResponse } from '../../api/twin'
import { Faq } from '../../pages/TwinGenerationPage/components/FaqTab/FaqTab'
import { Inputs } from '../../pages/TwinGenerationPage/components/ProfileTab/ProfileTab'

import { RootState } from '../store'

export interface IKnowledgeFile {
  id: string
  fileName: string
  fileType: string
  fileExtension: string
  status: 'uploading' | 'uploaded' | 'error'
  errorMessage?: string
  file: File | null
  uploadUrl?: string
  uploadId?: string
}

export interface twinState {
  // state for generation
  previewUrl: string | null
  audioUrl: string | null
  jobTitle: string | null
  firstName: string | null
  lastName: string | null
  title: string | null
  twinId: string | null
  interviewSessionId: string | null
  // state for image preview
  styledOriginUrls: Record<string, string>
  selectedImageStyle: string | null
  previewTabName: string
  previewOriginalUrl: string | null
  // state for linkedin profile
  profileJson: string | null
  linkedInSummary: string | null
  linkedInImageUrl: string | null
  // state from interview
  knowledge: string | null
  isCameraReady: boolean
  faqs: Faq[]
  errors: Record<string, string>
  // state for knowledge files
  uploadedKnowledge: Record<string, IKnowledgeFile>
}

const initialState: twinState = {
  previewUrl: null,
  audioUrl: null,
  jobTitle: null,
  firstName: null,
  lastName: null,
  title: null,
  twinId: null,
  interviewSessionId: null,
  styledOriginUrls: {},
  selectedImageStyle: null,
  previewTabName: '',
  previewOriginalUrl: null,
  profileJson: null,
  linkedInSummary: null,
  linkedInImageUrl: null,
  knowledge: null,
  isCameraReady: false,
  faqs: [],
  errors: {},
  uploadedKnowledge: {},
}

export const twinSlice = createSlice({
  name: 'digitalTwin',
  initialState,
  reducers: {
    setIsCameraReady: (state, action: PayloadAction<boolean>) => {
      state.isCameraReady = action.payload
    },
    setPreviewUrl: (state, action: PayloadAction<string>) => {
      state.previewUrl = action.payload
    },
    setStyledOriginUrls: (
      state,
      action: PayloadAction<Record<string, string>>,
    ) => {
      state.styledOriginUrls = {
        ...state.styledOriginUrls,
        ...action.payload,
      }
    },
    setSelectedImageStyle: (state, action: PayloadAction<string | null>) => {
      state.selectedImageStyle = action.payload
    },
    setPreviewTabName: (state, action: PayloadAction<string>) => {
      state.previewTabName = action.payload
    },
    setPreviewOriginalUrl: (state, action: PayloadAction<string | null>) => {
      state.previewOriginalUrl = action.payload
    },
    setLinkedInProfile: (
      state,
      action: PayloadAction<LinkedInProfileResponse>,
    ) => {
      state.profileJson = action.payload.profileJson
      state.linkedInSummary = action.payload.summary
      state.linkedInImageUrl = action.payload.profileImageUrl
    },
    setAudioUrl: (state, action: PayloadAction<string>) => {
      state.audioUrl = action.payload
    },
    setKnowledge: (state, action: PayloadAction<Record<string, string>>) => {
      state.knowledge = action.payload.knowledge
      state.title = action.payload.knowledge
      if (action.payload.jobTitle) {
        state.jobTitle = action.payload.jobTitle
      }
    },
    setUserData: (state, action: PayloadAction<Inputs>) => {
      state.firstName = action.payload.firstName
      state.lastName = action.payload.lastName
      state.jobTitle = action.payload.jobTitle
    },
    setTwinId: (state, action: PayloadAction<string | null>) => {
      state.twinId = action.payload
    },
    setInterviewSessionId: (state, action: PayloadAction<string | null>) => {
      state.interviewSessionId = action.payload
    },
    setFaqs: (state, action: PayloadAction<Faq[]>) => {
      state.faqs = action.payload
    },
    addFaq: (state, action: PayloadAction<Faq>) => {
      state.faqs.unshift(action.payload)
    },
    updateFaq: (state, action: PayloadAction<Faq>) => {
      const index = state.faqs.findIndex(faq => faq.id === action.payload.id)
      if (index !== -1) {
        state.faqs[index] = action.payload
      }
    },
    deleteFaq: (state, action: PayloadAction<string>) => {
      state.faqs = state.faqs.filter(faq => faq.id !== action.payload)
    },
    setTabError: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors[action.payload.tab] = action.payload.error
    },
    clearTabError: (state, action: PayloadAction<string>) => {
      delete state.errors[action.payload]
    },
    clearAllErrors: state => {
      state.errors = {}
    },
    resetTwinState: () => {
      return initialState
    },
    addKnowledgeFile: (state, action: PayloadAction<IKnowledgeFile>) => {
      state.uploadedKnowledge[action.payload.fileName] = action.payload
    },
    updateKnowledgeFileStatus: (
      state,
      action: PayloadAction<{
        fileName: string
        status: IKnowledgeFile['status']
        errorMessage?: string
        uploadUrl?: string
        file: File | null
        id: string
        uploadId?: string
      }>,
    ) => {
      const file = state.uploadedKnowledge[action.payload.fileName]
      if (file) {
        file.status = action.payload.status
        file.file = action.payload.file
        file.uploadUrl = action.payload.uploadUrl
        file.id = action.payload.id
        if (action.payload.errorMessage) {
          file.errorMessage = action.payload.errorMessage
        }
      }
    },
    removeKnowledgeFile: (state, action: PayloadAction<string>) => {
      delete state.uploadedKnowledge[action.payload]
    },
    setUploadedKnowledge: (
      state,
      action: PayloadAction<Record<string, IKnowledgeFile>>,
    ) => {
      state.uploadedKnowledge = action.payload
    },
  },
})

export const {
  setPreviewUrl,
  setStyledOriginUrls,
  setSelectedImageStyle,
  resetTwinState,
  setLinkedInProfile,
  setAudioUrl,
  setKnowledge,
  setUserData,
  setTwinId,
  setIsCameraReady,
  setInterviewSessionId,
  setFaqs,
  addFaq,
  updateFaq,
  deleteFaq,
  setTabError,
  clearTabError,
  clearAllErrors,
  setPreviewTabName,
  setPreviewOriginalUrl,
  addKnowledgeFile,
  updateKnowledgeFileStatus,
  removeKnowledgeFile,
  setUploadedKnowledge,
} = twinSlice.actions

export const getTwinState = (state: RootState) => state.twinReducer

export default twinSlice.reducer
